import {
  Component,
  effect,
  inject,
  model,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { InputBuscador } from '@shared/components/input-buscador/input-buscador';
import { UiButton, UiDialog, UiTooltip } from '@shared/components/ui';
import { Tabla } from '@shared/components/tabla/tabla';
import { ClienteApiService } from '@core/services/api/cliente-api.service';
import { Paginacion } from '@shared/components/paginacion/paginacion';
import { AppService } from '@shared/services/app.service';
import { CrearCliente } from '@shared/components/crear-cliente/crear-cliente';
import { IClienteData } from '@core/interfaces/entidades/cliente.entidad';
import { EditarCliente } from '@shared/components/editar-cliente/editar-cliente';
import { ModalConfirmacion } from '@shared/components/modal-confirmacion/modal-confirmacion';
import { ToastService } from '@shared/services/toast.service';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { TelefonoPipe } from '../../shared/pipes/telefono.pipe';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import { SucursalService } from '@shared/services/sucursal.service';

@Component({
  selector: 'app-cliente',
  imports: [
    InputBuscador,
    UiButton,
    UiDialog,
    UiTooltip,
    Tabla,
    Paginacion,
    CrearCliente,
    EditarCliente,
    ModalConfirmacion,
    TelefonoPipe,
  ],
  templateUrl: './cliente.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cliente.css',
})
export class Cliente implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cambiarPagina(1);
      }
    });
  }

  private itemsPorPagina = 18;
  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Clientes y Contactos',
      subtitle: 'Gestiona la información de tus clientes para ventas y seguimiento comercial.',
      showBackButton: true,
      icon: 'bi bi-person',
    });
    this.columnasTabla = [
      { key: 'dniRuc', titulo: 'DNI / RUC' },
      { key: 'nombre_completo', titulo: 'Nombre Completo' },
      { key: 'tipo_documento', titulo: 'Tipo Documento' },
      { key: 'direccion', titulo: 'Dirección' },
      { key: 'numero_celular', titulo: 'Número Celular', template: this.telefonoTemplate() },
    ];
  }
  // templates
  telefonoTemplate = viewChild('telefonoTemplate');
  // servicios
  private clienteApiService = inject(ClienteApiService);
  private appService = inject(AppService);
  private toastService = inject(ToastService);
  private sucursalService = inject(SucursalService);
  // datos
  busquedaCliente = model<string>('');
  modal_crear_cliente = model<boolean>(false);
  modal_editar_cliente = model<boolean>(false);
  modal_eliminar_cliente = model<boolean>(false);
  paginacion = signal<IPaginar<IClienteData> | null>(null);

  columnasTabla!: ITablaColumna[];

  // metodos
  onBuscarCliente() {
    if (this.busquedaCliente().trim() === '') {
      this.cambiarPagina(1);
    } else {
      this.clienteApiService
        .buscarCoincidencia(this.busquedaCliente().trim())
        .subscribe((response) => {
          this.paginacion.set({
            data: response,
            total: response.length,
            pagina: 1,
            size: this.itemsPorPagina,
          });
        });
    }
  }

  cambiarPagina(nuevaPagina: number) {
    this.clienteApiService.paginar(nuevaPagina, this.itemsPorPagina).subscribe((response) => {
      this.paginacion.set(response);
    });
  }

  clienteSeleccionado?: IClienteData;
  onCrear(data: IClienteData) {
    this.modal_crear_cliente.set(false);
    this.paginacion.set({
      data: [data, ...(this.paginacion()?.data || [])],
      total: (this.paginacion()?.total || 0) + 1,
      pagina: this.paginacion()?.pagina || 1,
      size: this.paginacion()?.size || this.itemsPorPagina,
    });
  }

  onEditar(cliente: IClienteData) {
    this.clienteSeleccionado = cliente;
    this.modal_editar_cliente.set(true);
  }
  onCerrarEditar(data?: IClienteData) {
    this.modal_editar_cliente.set(false);
    if (data) {
      const datosActualizados = this.paginacion()?.data.map((cliente) =>
        cliente.dniRuc === data.dniRuc ? data : cliente,
      );
      this.paginacion.set({
        data: datosActualizados || [],
        total: this.paginacion()?.total || 0,
        pagina: this.paginacion()?.pagina || 1,
        size: this.paginacion()?.size || this.itemsPorPagina,
      });
    }
  }

  eliminar() {
    if (!this.clienteSeleccionado) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'No se ha seleccionado ningún cliente para eliminar.',
      );
      return;
    }
    this.clienteApiService.eliminar(this.clienteSeleccionado.id).subscribe({
      next: () => {
        const datosActualizados = this.paginacion()?.data.filter(
          (cliente) => cliente.dniRuc !== this.clienteSeleccionado?.dniRuc,
        );
        this.paginacion.set({
          data: datosActualizados || [],
          total: (this.paginacion()?.total || 1) - 1,
          pagina: this.paginacion()?.pagina || 1,
          size: this.paginacion()?.size || this.itemsPorPagina,
        });
        this.toastService.show('top-right', 'success', 'Éxito', 'Cliente eliminado correctamente.');
        this.cerrarModalEliminar();
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al eliminar el cliente.',
        );
      },
    });
  }

  onEliminar(cliente: IClienteData) {
    this.clienteSeleccionado = cliente;
    this.modal_eliminar_cliente.set(true);
  }

  cerrarModalEliminar() {
    this.modal_eliminar_cliente.set(false);
    this.clienteSeleccionado = undefined;
  }
}
